import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { setPreference } from 'admin/my-account/MyAccount';
import { ProfileActionType, useProfile } from 'auth/Auth';
import { Tab } from 'common/chrome/left-nav/nav-tabs/NavTabs.types';
import { Overlay } from 'core/overlay/Overlay';
import { LocaleCode } from 'core/util/Locale';
import { Category, TrackActionType, useTrack } from 'core/util/Track';

import 'common/chrome/left-nav/upsell-picker/UpsellPicker.scss';

interface Props {
    tab: Tab;
    show: boolean;
    onClose: (e: MouseEvent) => void;
}

enum TrackEventName {
    Interested = 'upsell_interested_click',
    NotInterested = 'upsell_not_interested_click',
    View = 'upsell_product_view',
}

export const UpsellPicker: FunctionComponent<Props> = (props) => {
    const { tab, show, onClose } = props;
    const { t } = useTranslation(['common/chrome']);
    const [{ profile }, profileDispatch] = useProfile();
    const [, trackDispatch] = useTrack();

    const [chosen, setChosen] = useState(false);
    const [interested, setInterested] = useState(
        profile!.user.preferences && profile!.user.preferences[`upsell-${tab.name}`] === 'true',
    );
    const [notInterested, setNotInterested] = useState(
        profile!.user.preferences && profile!.user.preferences[`upsell-${tab.name}`] === 'false',
    );

    const onInterested = async (e: MouseEvent): Promise<void> => {
        // Track
        trackDispatch({
            type: TrackActionType.Track,
            event: {
                app: tab.name,
                name: TrackEventName.Interested,
                properties: {
                    productType: tab.name,
                    productLocalizedName: t(tab.name),
                    category: Category.Action,
                },
            },
        });

        const args = {
            preferenceKey: `upsell-${tab.name}`,
            preferenceValue: 'true',
        };

        try {
            const user = await trackPromise(setPreference(profile!.tenant!.id, profile!.user.id, args), 'modal');

            // Update local profile
            profile!.user.preferences = user.preferences;

            setChosen(true);
            setInterested(true);
        } catch {}
    };

    const onNotInterested = async (e: MouseEvent): Promise<void> => {
        trackDispatch({
            type: TrackActionType.Track,
            event: {
                app: tab.name,
                name: TrackEventName.NotInterested,
                properties: {
                    productType: tab.name,
                    productLocalizedName: t(tab.name),
                    category: Category.Action,
                },
            },
        });

        const args = {
            preferenceKey: `upsell-${tab.name}`,
            preferenceValue: 'false',
        };

        try {
            const user = await trackPromise(setPreference(profile!.tenant!.id, profile!.user.id, args), 'modal');

            // Update local profile
            profile!.user.preferences = user.preferences;

            setChosen(true);
            setNotInterested(true);
        } catch {}
    };

    const onReturn = (e: MouseEvent): void => {
        setChosen(false);
    };

    const onModalClose = (e: MouseEvent): void => {
        setChosen(false);
        onClose(e);

        profileDispatch({
            type: ProfileActionType.SetPreferenceSuccess,
            profile,
        });
    };

    const getVideoLink = (tab: Tab): string => {
        if (typeof tab?.upsell?.video === 'string') {
            return tab.upsell.video;
        }
        if (tab?.upsell?.video[profile!.user.locale]) {
            return tab?.upsell?.video[profile!.user.locale] || '';
        }
        return tab?.upsell?.video[LocaleCode.en_US] || '';
    };

    useEffect(() => {
        if (show) {
            // Track
            trackDispatch({
                type: TrackActionType.Track,
                event: {
                    app: tab.name,
                    name: TrackEventName.View,
                    properties: {
                        productType: tab.name,
                        productLocalizedName: t(tab.name),
                        category: Category.Action,
                    },
                },
            });
        }
    }, [show, trackDispatch, tab.name, t]);

    return (
        <Modal size="lg" show={show} onHide={onModalClose} className="r4e-upsell-picker">
            <Overlay area="modal" />
            <Modal.Header closeButton>
                <Modal.Title>&nbsp;</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!chosen ? (
                    <div className="preference">
                        <div className="description">
                            <h3>{t(tab.name)}</h3>
                            {
                                /* eslint-disable */
                                <div dangerouslySetInnerHTML={{ __html: t(tab.upsell!.description) }} />
                                /* eslint-enable */
                            }
                            {!interested && !notInterested ? (
                                <>
                                    <Button variant="primary" onClick={onInterested}>
                                        {t('UPSELL_IM_INTERESTED')}
                                    </Button>{' '}
                                    <Button variant="default" onClick={onNotInterested}>
                                        {t('UPSELL_IM_NOT_INTERESTED')}
                                    </Button>
                                </>
                            ) : null}

                            {interested ? (
                                <Button variant="primary" disabled>
                                    <i className="icon-check" /> <span>{t('UPSELL_IM_INTERESTED')}</span>
                                </Button>
                            ) : null}

                            {notInterested ? (
                                <Button variant="default" disabled>
                                    <i className="icon-check" /> <span>{t('UPSELL_IM_NOT_INTERESTED')}</span>
                                </Button>
                            ) : null}
                        </div>
                        <div className="video">
                            <iframe
                                src={getVideoLink(tab)}
                                frameBorder={0}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={tab.name}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="preference-confirmation">
                        <h3>{interested ? t('UPSELL_INTERESTED_TITLE') : t('UPSELL_NOT_INTERESTED_TITLE')}</h3>
                        <div>
                            {interested
                                ? t('UPSELL_INTERESTED_DESCRIPTION', { app: t(tab.name) })
                                : t('UPSELL_NOT_INTERESTED_DESCRIPTION', { app: t(tab.name) })}
                        </div>

                        {interested ? (
                            <Button variant="default" onClick={onReturn}>
                                {t('UPSELL_RETURN')}
                            </Button>
                        ) : null}

                        {notInterested ? (
                            <Button variant="default" onClick={onModalClose}>
                                {t('UPSELL_CLOSE')}
                            </Button>
                        ) : null}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>&nbsp;</Modal.Footer>
        </Modal>
    );
};
